import React, { useState, useRef, useEffect } from "react"
import DataLoader, { factors, years } from "../DataLoader"

import gradient from "../utils/gradient"
import "./GlobalInsightsHistorical.scss"
import { globalInsightsConstants } from "./"
import { scaleSqrt } from "d3-scale"
import Tooltip from "../Tooltip/"
import { useTransition, animated } from "react-spring"
import { useSmallWidthMode } from "../utils/smallWidthMode"
import getShortCountryLabel from "../utils/getShortCountryLabel"

export default function GlobalInsightsHistorical(props) {
  const [tooltipData, setTooltipData] = useState(null)

  const {
    selectedYear,
    selectedFactor,
    containerDimensions,
    selectedFilterIncome,
    selectedFilterRegion,
    selectedCountry,
    headerHeight,
  } = props
  const selectedCountryRef = useRef()
  const inSmallWidthMode = useSmallWidthMode()
  useEffect(() => {
    if (
      selectedCountry !== "" &&
      selectedCountryRef.current &&
      props.containerRef &&
      headerHeight
    ) {
      const scrollDomNode = inSmallWidthMode
        ? document.documentElement
        : props.containerRef
      const scrollOffset =
        selectedCountryRef.current.getBoundingClientRect().y +
        scrollDomNode.scrollTop -
        210 // 210 is about the size of the combined headers
      scrollDomNode.scrollTo({
        top: scrollOffset,
        behavior: "smooth",
      })
    }
  }, [selectedCountry, props.containerRef, inSmallWidthMode, headerHeight])

  let scrollToCountryNeeded = useRef(false)
  useEffect(() => {
    scrollToCountryNeeded.current = true
  }, [selectedFactor, selectedYear])

  // always grab the last year, as we want to show all countries
  let countries = [...DataLoader.dataByYear[selectedYear].countries]

  // since income & regions vary from year to year, these values might not make sense for the countries selected above
  if (selectedFilterIncome !== "") {
    countries = countries.filter(
      (country) => country["Income Group"] === selectedFilterIncome,
    )
  }
  if (selectedFilterRegion !== "") {
    countries = countries.filter(
      (country) => country.Region === selectedFilterRegion,
    )
  }
  countries = countries.map((country) => {
    return { Country: country.Country }
  })

  const factorSelected = selectedFactor !== ""
  const factorIndex = factorSelected ? factors.indexOf(selectedFactor) : -1

  const {
    rowHeight,
    labelRankPadding,
    sidePadding,
    barHeight,
    labelRankWidth,
  } = globalInsightsConstants

  const sidePaddingToUse = inSmallWidthMode ? labelRankPadding : sidePadding

  const svgHeight = countries.length * rowHeight
  const svgWidth =
    (containerDimensions.width ? containerDimensions.width : 300) -
    (inSmallWidthMode ? 0 : 32)

  const scoreKey = factorSelected
    ? `Factor ${factorIndex + 1}: ${selectedFactor}`
    : "overall"
  const selectedYearIndex = years.indexOf(selectedYear)
  const yearsToUse = years.slice(0, selectedYearIndex + 1)
  countries.forEach((country) => {
    years.forEach((year) => {
      const countryDatum = DataLoader.dataByYear[year].countries.find(
        (d) => d.Country === country.Country,
      )
      if (countryDatum) {
        country[year] = countryDatum
      }
    })
  })
  years.forEach((year) => {
    countries.sort((a, b) => {
      const bValue = b[year] ? b[year][scoreKey] : null
      const aValue = a[year] ? a[year][scoreKey] : null

      if (!aValue && !bValue) {
        return 0 //perhaps do an alphabetic comparison? nah we don't really care cause we don't display em anyway
      } else if (!aValue) {
        return 1
      } else if (!bValue) {
        return -1
      }
      return bValue - aValue
    })
    countries.forEach((country, countryIndex) => {
      country[`rank_${year}`] = countryIndex
    })
  })

  countries.sort((a, b) => {
    return a[`rank_${selectedYear}`] - b[`rank_${selectedYear}`]
  })

  const labelWidth = Math.max(
    svgWidth * globalInsightsConstants.labelWidthPercentage,
    globalInsightsConstants.minLabelWidth,
  )
  const fullBarWidth = svgWidth - labelWidth

  const yearWidth = (fullBarWidth - sidePaddingToUse) / yearsToUse.length
  const radiusScale = scaleSqrt().domain([0, 1]).range([2, 10])

  const countPerYear = {}
  years.forEach((year) => {
    countPerYear[year] = 0
    countries.forEach((country) => {
      const score = country[year]
      if (!score) {
        return
      }
      countPerYear[year]++
    })
  })

  const hoverCountry = (country, year, showTooltip = true) => {
    return (event) => {
      if (!country) {
        return setTooltipData(null)
      }

      let x = 0
      let y = 0
      if ("clientX" in event) {
        x = event.clientX
        y = event.clientY
      }
      if (event.touches) {
        event.preventDefault()
        event.stopPropagation()
      }
      setTooltipData({
        country: country[year],
        year,
        x,
        y,
        showTooltip,
        showHistoricalPlot: true,
      })
    }
  }

  const getTransitionValues = (country) => {
    const countryIndex = countries.findIndex((d) => d === country)

    const result = { y: countryIndex * rowHeight }
    const countryPoints = []
    const yearsWithData = {}
    yearsToUse.forEach((year, yearIndex) => {
      if (!country[year]) {
        return null
      }
      const score = country[year][scoreKey]
      if (!score) {
        return null
      }
      const x = yearWidth * yearIndex + yearWidth * 0.5 + sidePaddingToUse
      const yearRank = country[`rank_${year}`]

      // we can either position circles relative to the number of countries in that year
      // or always having a fixed height per row. varies if you want to have a large gap at the bottom
      // or have years line up across the entire graph

      // fixed row height
      const cy = yearRank * rowHeight + rowHeight * 0.5

      // variable row height depending on number of countries in each year
      // const cy = yearRank / countPerYear[year] * svgHeight + rowHeight * 0.5
      result[`cx-${year}`] = x
      result[`cy-${year}`] = cy
      yearsWithData[year] = true
      countryPoints.push([x, cy])
    })
    years.forEach((year, yearIndex) => {
      if (yearsWithData[year]) {
        return
      }
      let tries = 0
      let stop = true
      let xy = null
      let distance = Number.MAX_SAFE_INTEGER
      while (tries < years.length && stop) {
        const yearIndexToTry = tries
        const yearToTry = years[yearIndexToTry]
        let potentialDistance = Math.abs(yearIndexToTry - yearIndex)
        if (yearsWithData[yearToTry] && potentialDistance < distance) {
          potentialDistance = distance
          xy = [result[`cx-${yearToTry}`], result[`cy-${yearToTry}`]]
        }
        tries++
      }
      if (!xy) {
        console.warn("something went wrong")
      }
      countryPoints.push(xy)
      result[`cx-${year}`] = xy[0]
      result[`cy-${year}`] = xy[1]
    })
    const countryLine = countryPoints.map((point) => point.join(",")).join(" ")
    result.countryLine = countryLine
    // console.log(result)
    return result
  }

  const totalTransitions = countries.length
  let numberTransitionsCompleted = 0

  const transitions = useTransition(countries, (d) => d.Country, {
    enter: getTransitionValues,
    update: getTransitionValues,
    onRest: () => {
      numberTransitionsCompleted++
      if (numberTransitionsCompleted === totalTransitions) {
        if (scrollToCountryNeeded.current) {
          scrollToCountryNeeded.current = false
          if (
            selectedCountry !== "" &&
            selectedCountryRef.current &&
            props.containerRef
          ) {
            const scrollOffset =
              selectedCountryRef.current.getBoundingClientRect().y +
              props.containerRef.scrollTop -
              210 // 210 is about the size of the combined headers
            props.containerRef.scrollTo({
              top: scrollOffset,
              behavior: "smooth",
            })
          }
        }
      }
    },
  })

  const countryGroups = transitions.map((transition) => {
    if (transition.state === "leave") {
      return null
    }
    const country = transition.item
    const countryIndex = countries.findIndex((d) => d === country)

    const { y } = transition.props
    const textY = barHeight * 0.75
    const topMargins = (rowHeight - barHeight) / 2
    const yearsCircles = yearsToUse.map((year, yearIndex) => {
      if (!country[year]) {
        return null
      }
      const score = country[year][scoreKey]
      if (!score) {
        return null
      }
      const x = transition.props[`cx-${year}`]
      const y = transition.props[`cy-${year}`]

      const fill = gradient(score)
      const r = radiusScale(score)
      return (
        <g
          key={year}
          onClick={props.gotoCountryPage(country.Country)}
          onMouseOver={hoverCountry(country, year)}
          onMouseOut={hoverCountry(null)}
          onMouseMove={hoverCountry(country, year)}
          onTouchEnd={hoverCountry(country, year)}
        >
          <animated.circle fill={fill} cx={x} cy={y} r={r} stroke="#56565A" />
        </g>
      )
    })

    const countryLabelWeight =
      country.Country === selectedCountry ||
      (tooltipData !== null && tooltipData.country.Country === country.Country)
        ? "900"
        : null
    const countryTextStyle = {
      fontWeight: countryLabelWeight,
      cursor: "pointer",
    }

    let opacity = null
    // let strokeWidth = null
    if (selectedCountry !== "") {
      opacity = country.Country === selectedCountry ? opacity : 0.3
      // strokeWidth = country.Country === selectedCountry ? 2 : strokeWidth
    }
    if (tooltipData !== null) {
      opacity = tooltipData.country.Country === country.Country ? null : 0.2
      // strokeWidth = tooltipData.country.Country === country.Country ? 2 : strokeWidth
    }

    return (
      <g
        key={country.Country}
        opacity={opacity}
        ref={selectedCountry === country.Country ? selectedCountryRef : null}
      >
        <animated.g
          transform={y.interpolate((y) => `translate(0, ${y + topMargins})`)}
        >
          <text
            className="textLabel"
            onMouseOver={hoverCountry(country, selectedYear, false)}
            onMouseOut={hoverCountry(null)}
            onMouseMove={hoverCountry(country, selectedYear, false)}
            onClick={() => props.updateUrl("country")(country.Country)}
            x={fullBarWidth + labelRankPadding * 2 + labelRankWidth}
            y={textY}
            style={countryTextStyle}
          >
            {country.Country}
          </text>
          <text
            className="textLabel"
            onMouseOver={hoverCountry(country, selectedYear, false)}
            onMouseOut={hoverCountry(null)}
            onMouseMove={hoverCountry(country, selectedYear, false)}
            onClick={() => props.updateUrl("country")(country.Country)}
            x={fullBarWidth + labelRankPadding}
            y={textY}
            style={countryTextStyle}
          >
            {countryIndex + 1}
          </text>
          <text
            className="textLabel smallWidthMode"
            onMouseOver={hoverCountry(country, selectedYear, false)}
            onMouseOut={hoverCountry(null)}
            onMouseMove={hoverCountry(country, selectedYear, false)}
            onClick={() => props.updateUrl("country")(country.Country)}
            x={fullBarWidth + labelRankPadding}
            y={textY}
            style={countryTextStyle}
          >
            {countryIndex + 1} {getShortCountryLabel(country.Country)}
          </text>
        </animated.g>
        {/* <animated.polyline points={countryLine} stroke="rgba(0, 0, 0, 0.5)" fill='none' strokeWidth={strokeWidth} /> */}
        {yearsCircles}
      </g>
    )
  })

  const historicalHeaderHeight = 24
  const headers = yearsToUse.map((year, yearIndex) => {
    const x = yearIndex * yearWidth + sidePaddingToUse

    return (
      <g key={year} transform={`translate(${x}, 0)`}>
        <rect width={yearWidth} height={24} fill="#CECECE" stroke="#cecece" />
        <text
          y={18}
          x={yearWidth / 2}
          textAnchor="middle"
          fill="#56565A"
          style={{ fontWeight: "700" }}
        >
          {year}
        </text>
        {year === selectedYear ? (
          <rect
            width={yearWidth}
            height={historicalHeaderHeight}
            fill="rgba(0, 0, 0, 0.05)"
          />
        ) : null}
      </g>
    )
  })
  const backgroundRectRowCount = 10
  const numberOfBackgroundRects = Math.ceil(
    countries.length / backgroundRectRowCount,
  )
  const backgroundRects = []
  for (let i = 0; i <= numberOfBackgroundRects; i++) {
    backgroundRects.push(
      <rect
        key={i}
        y={rowHeight * backgroundRectRowCount * i}
        width={svgWidth - sidePaddingToUse}
        x={sidePaddingToUse}
        height={rowHeight * backgroundRectRowCount}
        fill="url(#historicalTableBackgroundGradient)"
      />,
    )
  }
  const headerTop = inSmallWidthMode
    ? document.querySelector(".Nav").getBoundingClientRect().height
    : null

  return (
    <div className="globalInsightsHistorical">
      <div className="graphTitle" style={{ marginLeft: sidePaddingToUse }}>
        Countries Ranked by {factorSelected ? selectedFactor : "Overall Score"}
      </div>
      <div className="sticky" style={{ top: headerTop }}>
        <svg
          height={historicalHeaderHeight}
          width={svgWidth}
          className="headers"
        >
          {headers}
        </svg>
      </div>
      <svg height={svgHeight} width={svgWidth}>
        <defs>
          <linearGradient
            id="historicalTableBackgroundGradient"
            y2="100%"
            x2="0%"
          >
            <stop offset="0" stopColor="#f2f2f2" />
            <stop offset="20%" stopColor="#fff" />
          </linearGradient>
        </defs>
        {backgroundRects}
        {countryGroups}
        <rect
          x={sidePaddingToUse + selectedYearIndex * yearWidth}
          width={yearWidth}
          height={countries.length * rowHeight}
          fill="rgba(0, 0, 0, 0.05)"
          pointerEvents="none"
        />
      </svg>
      {tooltipData && tooltipData.showTooltip ? (
        <Tooltip
          {...tooltipData}
          scoreKey={scoreKey}
          selectedYear={tooltipData.year}
          closeTooltip={hoverCountry(null)}
          gotoDetails={props.gotoCountryPage(tooltipData.country.Country)}
        />
      ) : null}
    </div>
  )
}
